import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { ItemIconChain } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { openLockedFeatureHook } from "app/components/React/QuickSearch/pageData/openLockedFeatureHook";
import type { Recent } from "app/custom-hooks/useRecentsHook";
import { createWebsiteUrlFromRecent } from "app/pages/activation/components/HomeSearch/helpers/createWebsiteUrlFromRecent";
import { redirectToUrl } from "app/pages/activation/components/HomeSearch/helpers/redirectToUrl";
import { recentActivityDataAutomation } from "app/pages/activation/components/HomeWithData/dataAutomationKeys";
import { getRecentWebsiteData } from "app/pages/activation/components/HomeWithData/helpers/getRecentWebsiteData";
import { MORE_COMPETITORS } from "app/pages/activation/components/HomeWithData/texts";
import { activationTracking } from "app/pages/activation/services/tracking";

import { RecentItemTitle } from "./RecentItemTitle";
import * as SC from "./styles";

interface IRecentWebsiteItemProps {
    item: Recent;
    isLocked?: boolean;
}

export const RecentWebsiteItem = ({ item, isLocked = false }: IRecentWebsiteItemProps) => {
    const { translate } = useTranslate();
    const track = activationTracking.homePageWithData;

    const { icons, mainItem, otherCount, firstCompetitor, competitorsLength, pageTitle, stateName } =
        getRecentWebsiteData(item);

    const url = createWebsiteUrlFromRecent(item, stateName);

    const onClick = () => {
        if (isLocked) {
            openLockedFeatureHook(stateName);
            return;
        }
        track.trackClickRecent(url);
        redirectToUrl(url);
    };

    return (
        <SC.Item
            onClick={onClick}
            data-automation={recentActivityDataAutomation.websiteItem}
        >
            <SC.IconContainer
                hasOneIcon={!competitorsLength}
                data-automation={recentActivityDataAutomation.itemIcon}
            >
                <ItemIconChain
                    icons={icons}
                    size={20}
                    maxIconChain={2}
                    counterBoxWidth={20}
                />
            </SC.IconContainer>

            <SC.InfoContainer>
                <RecentItemTitle
                    pageTitle={pageTitle}
                    isLocked={isLocked}
                    dataAutomation={recentActivityDataAutomation.itemPage}
                />

                <SC.CompetitorsContainer data-automation={recentActivityDataAutomation.itemSearchResults}>
                    {mainItem}

                    {competitorsLength ? (
                        <>
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon[200]}
                            >
                                Vs.
                            </Text>

                            {firstCompetitor}

                            {competitorsLength > 1 ? (
                                <Text
                                    fontVariant="body2"
                                    color={colorsPalettes.carbon[200]}
                                >
                                    +{translate(MORE_COMPETITORS, { count: otherCount })}
                                </Text>
                            ) : null}
                        </>
                    ) : null}
                </SC.CompetitorsContainer>
            </SC.InfoContainer>
        </SC.Item>
    );
};
